import type { KeyAsString } from "type-fest";

type Proxies<T> = {
  [Key in keyof T as Uncapitalize<Key & string>]: T[Key]
};

type SetupFunction<T> = (m: T | undefined) => void;
type AnyFunction = (...args: unknown[]) => unknown;
type FunctionMap<T> = { [P in keyof T]: T[P] };
type ProxiesWithSetup<T> = Proxies<T> & { setup: SetupFunction<T> };

function noImpl<T>(methodName: keyof T) {
  console.error(`Attempted to call bounded dotnet method '${methodName.toString()}' but implementation is missing.`);
}

const uncapitalize = (s: string) => (s.length > 0 ? s[0]!.toLowerCase() + s.slice(1) : s);

export function createProxies<T extends FunctionMap<T>>(methods: KeyAsString<T>[]): ProxiesWithSetup<T> {
  const methodMap = new Map<string, AnyFunction>();

  const createProxy = (methodName: string) => {
    let _impl: AnyFunction | undefined;

    return (...args: unknown[]) => {
      if (_impl) {
        return _impl(...args);
      }
      const method = methodMap.get(methodName);
      if (method) {
        _impl = method;
        return _impl(...args);
      }
      return noImpl(methodName);
    };
  };

  const proxies = methods.reduce((acc, method) => {
    const methodLowerCase = uncapitalize(method);
    acc[methodLowerCase] = createProxy(method);
    return acc;
  }, {} as Record<string, AnyFunction>);

  const setup: SetupFunction<T> = (implementations: FunctionMap<T> | undefined) => {
    if (!implementations) return;

    for (const methodName in implementations) {
      const impl = implementations[methodName];
      const methodLowerCase = uncapitalize(methodName);
      if (proxies[methodLowerCase]) {
        methodMap.set(methodName, impl);
      }
    }
  };

  return {
    ...(proxies as Proxies<T>),
    setup,
  };
}
